/**
 * Feature Flag Service
 *
 * Manages feature flags:
 * - Reads API on app startup (requires authentication)
 * - Single API call returns all features
 * - All checks use in-memory cache (no localStorage for security)
 * - Features are fetched fresh on each app load
 *
 * Usage:
 * - Guards: FeatureFlagGuard wraps components for conditional rendering
 * - Hooks: usePamFeature() for reactive feature checking
 * - Direct: featureFlagService.isEnabled() for immediate checks
 *
 * Security Note:
 * - localStorage removed to prevent client-side manipulation
 * - All feature checks validated against backend on app initialization
 */

import { FEATURE_MAPPINGS, API_CONFIG } from "./featureFlagConfig";
import { isGamePassFeatureEnabled } from "../utils/gamePassFeature";
// TODO(PAMENG-1820): remove when the rollout fallback in `isProductActive`
// is deleted (see the JSDoc on that method for the full release checklist).
import { isDevModeEnv } from "../hooks/useIsDevMode";
import PublicAdminPanelInstance from "../api/PublicAdminPanelInstance";

const {
  PLATFORM_CONFIG_ENDPOINT,
  PLATFORM_CONFIG_V2_ENDPOINT,
  USE_API,
} = API_CONFIG;

/**
 * FeatureFlagService
 * Initialize once on app startup
 * Single API call fetches all features
 * Cache data in-memory only (no localStorage for security)
 * All checks read from memory (instant)
 * Fetches fresh from API on each app load
 */
class FeatureFlagService {
  constructor() {
    this.initialized = false; // v1 features hydrated/fetched
    this.productsInitialized = false; // v2 products fetch attempted (success OR failure)
    this.features = {};
    this.featuresConfig = {}; // Raw features_status_config from API
    this.products = {}; // Trivelta Products from /platform/v2/config; presence = enabled
  }

  /**
   * Get auth token from localStorage
   */
  getToken() {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      return token?.AccessToken || null;
    } catch (error) {
      console.error("Error getting token:", error);
      return null;
    }
  }

  /**
   * Helper: Get nested value from object using dot notation path
   * @param {Object} obj - Object to traverse
   * @param {string} path - Dot notation path (e.g., 'admin_panel_features.reports')
   * @returns {*} Value at path, or undefined if not found
   */
  getNestedValue(obj, path) {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  }

  /**
   * Fetch platform config from API (single call)
   * Returns all features in one response
   */
  async fetchFromAPI() {
    const token = this.getToken();
    if (!token) throw new Error("No authentication token");

    try {
      const response = await PublicAdminPanelInstance.get(
        PLATFORM_CONFIG_ENDPOINT,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const apiResponse = response?.data;

      // Validate response structure
      if (!apiResponse.success || !apiResponse.data) {
        throw new Error("Invalid API response structure");
      }

      // Store raw features config
      this.featuresConfig = apiResponse.data.features_status_config || {};

      // Parse features using mappings
      const features = {};
      Object.entries(FEATURE_MAPPINGS).forEach(
        ([frontendName, backendPath]) => {
          const value = this.getNestedValue(this.featuresConfig, backendPath);
          features[frontendName] = value === true; // Ensure boolean
        }
      );

      // Composite: PAM is reachable when either consumer or PAM flag is on.
      features.gamePass = isGamePassFeatureEnabled(this.featuresConfig);

      return features;
    } catch (error) {
      console.error("Error fetching from API:", error);
      throw error;
    }
  }

  /**
   * Fetch Trivelta Products from /admin-panel/platform/v2/config.
   * Response shape: { data: { products: { <scope_key>: { integrations, feature_flags } } } }
   * Only enabled products appear — absence = disabled.
   */
  async fetchProductsFromAPI() {
    const token = this.getToken();
    if (!token) throw new Error("No authentication token");

    const response = await PublicAdminPanelInstance.get(
      PLATFORM_CONFIG_V2_ENDPOINT,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const apiResponse = response?.data;
    if (!apiResponse?.success || !apiResponse?.data) {
      throw new Error("Invalid v2 config response structure");
    }

    return apiResponse.data.products || {};
  }

  /**
   * Initialize the feature flag service
   * Fetches feature flags from API on every app load.
   * No localStorage caching for security reasons.
   */
  async initialize() {
    // Fully loaded — both v1 features and v2 products cached.
    if (
      this.initialized &&
      Object.keys(this.featuresConfig).length > 0 &&
      this.productsInitialized
    ) {
      return this.features;
    }

    // Post-login path: v1 is hydrated but v2 hasn't been fetched. Grab v2
    // now. On failure keep `productsInitialized = false` so the next call
    // retries (matches `hydrateProductsFromV2(null)`).
    if (
      this.initialized &&
      Object.keys(this.featuresConfig).length > 0 &&
      !this.productsInitialized
    ) {
      if (this.getToken()) {
        try {
          this.products = await this.fetchProductsFromAPI();
          this.productsInitialized = true;
        } catch (err) {
          console.error("Error fetching Trivelta Products:", err);
          this.products = {};
        }
      } else {
        this.productsInitialized = true;
      }
      return this.features;
    }

    // If initialized with fallback values but token is now available, force refresh
    if (this.initialized && Object.keys(this.featuresConfig).length === 0 && this.getToken()) {
      this.initialized = false;
      this.productsInitialized = false;
      this.features = {};
      this.featuresConfig = {};
      this.products = {};
    }

    // If already initialized (with fallback) and no token, return fallback values
    if (this.initialized && !this.getToken()) {
      return this.features;
    }

    try {
      // Fetch from API (requires authentication)
      const token = this.getToken();
      if (!USE_API || !token) {
        // Pre-login: safe fallback. `productsInitialized` stays false so the
        // post-login init picks up v2.
        this.features = {
          reports: false,
          promotions: false,
          featureManagement: false,
          finixDelayedPayment: false,
          transactions: false,
          playerManagement: false,
          transactionsOverview: false,
          transactionsWallet: false,
          transactionsReports: false,
        };
        this.initialized = true;
        return this.features;
      }

      // Fetch v1 + v2 in parallel. v2 catches its own errors so a v2 outage
      // doesn't sink v1. If v2 fails we keep `productsInitialized = false` so
      // the next `initialize()` retries.
      const [features, productsResult] = await Promise.all([
        this.fetchFromAPI(),
        this.fetchProductsFromAPI()
          .then((p) => ({ ok: true, products: p }))
          .catch((err) => {
            console.error("Error fetching Trivelta Products:", err);
            return { ok: false, products: {} };
          }),
      ]);
      this.features = features;
      this.products = productsResult.products;
      this.initialized = true;
      if (productsResult.ok) {
        this.productsInitialized = true;
      }
      return this.features;
    } catch (error) {
      // Only log actual API errors, not expected "no token" scenarios
      if (error.message && !error.message.includes("no token found")) {
        console.error("Error initializing feature flags:", error);
      }

      // Fallback - default safe values (all disabled)
      this.features = {
        reports: false,
        promotions: false,
        featureManagement: false,
        finixDelayedPayment: false,
        transactions: false,
        playerManagement: false,
        transactionsOverview: false,
        transactionsWallet: false,
        transactionsReports: false,
      };
      this.products = {};

      this.initialized = true;
      this.productsInitialized = true;
      return this.features;
    }
  }

  /**
   * Hydrate the in-memory cache from a `features_status_config` payload the
   * caller already owns (typically the Redux `appConfig` slice fetched right
   * after login or on app startup). Mirrors `fetchFromAPI`'s mapping step,
   * minus the HTTP request.
   *
   * Why this exists: the singleton auto-initializes on module load before
   * authentication, falling back to a hard-coded `false` map that omits keys
   * such as `sportsbook_v2`, `casino`, `poker`, etc. After login the caller
   * already has the authoritative platform config in hand and must push the
   * FULL mapping through — any partial patch that leaves `initialized` true
   * with `featuresConfig` non-empty would convince subsequent `initialize()`
   * calls to short-circuit (see line 122) before refetching the missing
   * keys, leaving sider tabs gated by `<FeatureFlagGuard feature="…">`
   * hidden until a hard refresh re-runs the cold-start path.
   *
   * Replaces the in-memory state wholesale (no merge): we have the full
   * config, so the previous fallback map must be discarded entirely.
   *
   * @param {Object|null|undefined} featuresStatusConfig
   */
  hydrateFromPlatformConfig(featuresStatusConfig) {
    if (!featuresStatusConfig) {
      return;
    }

    this.featuresConfig = featuresStatusConfig;

    const features = {};
    Object.entries(FEATURE_MAPPINGS).forEach(
      ([frontendName, backendPath]) => {
        const value = this.getNestedValue(this.featuresConfig, backendPath);
        features[frontendName] = value === true;
      },
    );
    features.gamePass = isGamePassFeatureEnabled(this.featuresConfig);
    features.gamePassPam =
      this.featuresConfig.additional_features?.game_pass_pam === true;

    this.features = features;
    this.initialized = true;
  }

  /**
   * v2 equivalent of `hydrateFromPlatformConfig` — called by App.js/LogIn.js
   * with the pre-fetched v2 products so guards short-circuit without a
   * second network call. Pass `null` when the v2 fetch failed: we skip the
   * hydrate and leave `productsInitialized = false` so the next
   * `initialize()` retries.
   *
   * @param {Object|null|undefined} products
   */
  hydrateProductsFromV2(products) {
    if (products == null) {
      return;
    }
    this.products = products;
    this.productsInitialized = true;
  }

  /**
   * @param {string} featureName - The feature name to check (e.g., 'reports', 'promotions')
   * @returns {boolean} True if feature is enabled, false otherwise
   * @example
   * const reportsEnabled = featureFlagService.isEnabled('reports');
   * if (reportsEnabled) { showReportsButton(); }
   */
  isEnabled(featureName) {
    return this.features[featureName] ?? false;
  }

  /**
   * Get all features as an object
   *
   * @returns {Object} Copy of all features { featureName: boolean }
   * @example
   * const allFeatures = featureFlagService.getAllFeatures();
   * // Returns: { reports: false, promotions: true }
   */
  getAllFeatures() {
    return { ...this.features };
  }

  /**
   * Get raw features_status_config
   * Access to the complete nested structure from the API
   *
   * @returns {Object} Complete features_status_config object
   * @example
   * const config = featureFlagService.getFeaturesConfig();
   * // Returns: { admin_panel_features: {...}, payment_providers: {...}, ... }
   */
  getFeaturesConfig() {
    return { ...this.featuresConfig };
  }

  /**
   * Patch a nested value in the cached features_status_config using dot notation.
   *
   * The platform config is fetched once at startup and never re-fetched within a
   * session (see initialize()). When a value carried by the config is also written
   * through a dedicated endpoint, the in-memory snapshot would otherwise go stale.
   * Use this to keep the cache coherent with such writes so config fast-paths don't
   * surface pre-save values on a later read.
   *
   * @param {string} path - Dot notation path (e.g. 'kyc_providers.surt_checkpoints')
   * @param {*} value - Value to set at the path
   * @example
   * featureFlagService.patchFeaturesConfig('kyc_providers.surt_checkpoints', snap);
   */
  patchFeaturesConfig(path, value) {
    const keys = path.split(".");
    const next = { ...this.featuresConfig };
    let cursor = next;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
    cursor[keys[keys.length - 1]] = value;
    this.featuresConfig = next;
  }

  /**
   * Force refresh all config from API
   * Clears in-memory cache and re-fetches from the API
   * @returns {Promise<Object>} Fresh features object
   * @example
   * await featureFlagService.forceRefresh(); // Re-fetches from API
   */
  async forceRefresh() {
    this.initialized = false;
    this.productsInitialized = false;
    return await this.initialize();
  }

  /**
   * True if the product is active for the current client per
   * `/admin-panel/platform/v2/config` (presence in the response = enabled).
   *
   * Non-dev without `legacyFallbackFeature` returns `true` — routes gated by
   * that product stay open in prod if BE ever removes it. Only safe for
   * products that were never flag-gated (Operations Platform); every other
   * product MUST pass a fallback.
   *
   * TODO(PAMENG-1820): once the v2 catalog is authoritative everywhere,
   * replace this body with `Boolean(this.products?.[productKey])` and drop
   * `legacyFallbackFeature` at every callsite. See release checklist below.
   *
   * Release steps:
   *   1. Simplify body → `return Boolean(this.products?.[productKey]);`
   *   2. Drop the second arg at every `isProductActive(` callsite (KPIs.js,
   *      useFeatureFlag.js, UserCard.jsx, useTriveltaProduct.ts).
   *   3. See FeatureFlagGuard.js for the matching component-level cleanup.
   *
   * @param {string} productKey - Product scope_key. Use `TRIVELTA_PRODUCTS`.
   * @param {string} [legacyFallbackFeature] - Legacy feature flag consulted
   *   in non-dev envs during rollout.
   * @returns {boolean}
   */
  isProductActive(productKey, legacyFallbackFeature) {
    if (isDevModeEnv()) {
      return this.isProductKeyActive(productKey);
    }
    // Non-dev: rollout fallback. See TODO(PAMENG-1820) above.
    if (legacyFallbackFeature) return this.isEnabled(legacyFallbackFeature);
    return true;
  }

  /**
   * True if `productKey` is present in the v2 products catalog
   * (`/admin-panel/platform/v2/config`). Presence = enabled.
   *
   * Unlike `isProductActive`, this never consults `isDevModeEnv` or a
   * legacy feature-flag fallback — it is the catalog-only check.
   *
   * @param {string} productKey - Product scope_key. Use `TRIVELTA_PRODUCTS`.
   * @returns {boolean}
   */
  isProductKeyActive(productKey) {
    return Boolean(this.products && this.products[productKey]);
  }

  /**
   * Get the enabled integrations for a product (empty list if product is disabled).
   * @param {string} productKey
   * @returns {string[]}
   */
  getProductIntegrations(productKey) {
    return this.products?.[productKey]?.integrations ?? [];
  }

  /**
   * Get the enabled feature flags for a product (empty list if product is disabled).
   * @param {string} productKey
   * @returns {string[]}
   */
  getProductFeatureFlags(productKey) {
    return this.products?.[productKey]?.feature_flags ?? [];
  }

  /**
   * Get all active products keyed by scope_key.
   * @returns {Object} { [scopeKey]: { integrations: string[], feature_flags: string[] } }
   */
  getProducts() {
    return { ...this.products };
  }

  /**
   * Clear all caches
   * Resets in-memory state for features
   * @example
   * featureFlagService.clearCache(); // Forces fresh load on next check
   */
  clearCache() {
    this.initialized = false;
    this.productsInitialized = false;
    this.features = {};
    this.featuresConfig = {};
    this.products = {};
  }
}

/**
 * Singleton instance of FeatureFlagService
 *
 * Auto-initializes on first import to ensure features are loaded
 * when the app starts. All components should use this instance.
 *
 * @type {FeatureFlagService}
 */
const featureFlagService = new FeatureFlagService();

// Auto-initialize on first import to load features immediately
featureFlagService.initialize();

export default featureFlagService;
